import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import PixelLogo from '../shared/PixelLogo';
import type { StampWindow } from '../../services/shop-service';

type FloatingText = { id: number; x: number; y: number; text: string };

interface BrewStationProps {
  shopName: string | null;
  shopImageUrl?: string;
  shopPixelImageUrl?: string;
  isWaiting: boolean;
  hasShop: boolean;
  firstStamp?: boolean;
  accentColor?: string | null;  // shop's storefront tint (040)
  currencyLabel?: string;       // per-shop points word (040); default BEANS
  stampWindows?: StampWindow[] | null; // S3 happy hours — badge only, server decides
  offline?: boolean;  // T11/#10b — connectivity lost; replaces the whole station with a retry prompt
  paused?: boolean;   // T11/#9b — session-recovery banner is showing; mug goes inert, no shop chrome change
  statusLine?: string; // most recent activity-log line (cooldown/denied/expired/etc) — the
                        // locked first-visit screen has no other surface for it (predates
                        // the coral redesign; ported from the old FirstVisitLayout)
  onBrew: () => void;
  onFindShop: () => void;
  onRetry?: () => void; // T11/#10b — "Try again" pill; only meaningful when offline
}

const DAY_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

// Device-local approximation of the shop-local window (a customer standing in
// the shop shares its timezone). The webhook is the authority on multipliers.
function windowBadge(windows: StampWindow[] | null | undefined): string | null {
  if (!Array.isArray(windows) || windows.length === 0) return null;
  const now = new Date();
  const day = now.getDay();
  const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const active = windows.find((w) => w.day === day && w.start <= hhmm && hhmm < w.end);
  if (active) return `⚡ 2X STAMPS NOW — TIL ${active.end}`;
  // Next window in the coming 7 days, nearest first.
  const upcoming = [...windows].sort((a, b) =>
    (((a.day - day + 7) % 7) || 7) - (((b.day - day + 7) % 7) || 7) || a.start.localeCompare(b.start));
  const next = upcoming.find((w) => w.day !== day || w.start > hhmm) || upcoming[0];
  if (!next) return null;
  const dayLabel = next.day === day ? 'TODAY' : DAY_SHORT[next.day];
  return `⚡ 2X STAMPS ${dayLabel} ${next.start}–${next.end}`;
}

// Coral pixel-mug — mockup #5a (idle), #7a (tap-to-brew hint), #10a (waiting).
// #111111/coral for the live cup, #a8a29e/#d4cdb8 for the waiting cup — the
// mockup's honest-waiting palette so nobody mistakes it for a broken button.
function BrewMug({ isWaiting, active, onClick }: { isWaiting: boolean; active: boolean; onClick: (e: React.MouseEvent) => void }) {
  const ink = isWaiting ? '#a8a29e' : '#111111';
  const shadow = isWaiting ? '#d4cdb8' : '#f96a55';
  const fill = isWaiting ? '#f6f1e4' : '#ffffff';

  return (
    <div
      data-testid="brew-mug"
      onClick={onClick}
      // w-24 = the cup body's own width, so the flex parent centres the CUP
      // (its left edge lines up with the 96px shop logo above — Nik's
      // on-device call 2026-08-06); the handle overhangs to the right.
      className={`relative w-24 h-[112px] select-none ${active && !isWaiting ? 'cursor-pointer' : ''}`}
    >
      {/* Steam — idle only, mirrors the mockup's two drifting bars */}
      {active && !isWaiting && (
        <>
          <motion.div
            animate={{ y: [0, -24, 0], opacity: [0, 1, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}
            className="absolute -top-6 left-6 w-3 h-6 bg-body-grey"
          />
          <motion.div
            animate={{ y: [0, -30, 0], opacity: [0, 1, 0] }}
            transition={{ repeat: Infinity, duration: 2.5, delay: 0.5, ease: 'linear' }}
            className="absolute -top-[34px] left-12 w-3 h-[30px] bg-body-grey"
          />
        </>
      )}

      {/* Cup body */}
      <div
        className="absolute top-0 left-0 w-24 h-24 rounded-[8px] border-[5px]"
        style={{ borderColor: ink, background: fill, boxShadow: `6px 6px 0px ${shadow}` }}
      >
        <div
          className="absolute top-0 left-0 right-0 h-3 border-b-[5px]"
          style={{ borderColor: ink, background: shadow }}
        />
        {isWaiting ? (
          <>
            <div className="absolute top-9 left-6 w-3 h-3" style={{ background: ink }} />
            <div className="absolute top-9 right-6 w-3 h-3" style={{ background: ink }} />
            <div className="absolute top-16 left-9 w-6 h-2" style={{ background: ink }} />
          </>
        ) : (
          <>
            <div className="absolute top-6 left-3 w-3 h-3 bg-accent-alt" />
            <div className="absolute top-12 left-3 w-3 h-6 bg-accent-alt" />
            <div className="absolute top-9 left-6 w-3 h-3 bg-primary" />
            <div className="absolute top-9 right-6 w-3 h-3 bg-primary" />
            <div className="absolute top-[60px] left-9 w-6 h-3 bg-primary" />
          </>
        )}
        {/* Handle */}
        <div
          className="absolute top-3 -right-9 w-9 h-12 rounded-r-[8px] border-[5px] border-l-0"
          style={{ borderColor: ink, boxShadow: `6px 6px 0px ${shadow}` }}
        />
      </div>
    </div>
  );
}

export default function BrewStation({ shopName, shopImageUrl, shopPixelImageUrl, isWaiting, hasShop, firstStamp, accentColor, currencyLabel, stampWindows, offline, paused, statusLine, onBrew, onFindShop, onRetry }: BrewStationProps) {
  const [floatingTexts, setFloatingTexts] = useState<FloatingText[]>([]);
  const floatCounter = useRef(0);

  function handleClick(e: React.MouseEvent) {
    if (!hasShop || isWaiting || paused) return;

    const id = floatCounter.current++;
    const offsetX = (Math.random() - 0.5) * 40;
    setFloatingTexts((prev) => [...prev, { id, x: e.clientX + offsetX, y: e.clientY, text: `+10 ${currencyLabel || 'BEANS'}` }]);
    setTimeout(() => {
      setFloatingTexts((prev) => prev.filter((ft) => ft.id !== id));
    }, 1000);

    onBrew();
  }

  // Offline (#10b): no shop chrome at all — the mockup drops the logo/name
  // block entirely and centres a grey inert mug with its own retry prompt.
  // Reuses BrewMug's existing isWaiting=true palette (same greys as the
  // barista-wait state) rather than inventing a third mug treatment.
  //
  // Composed with paused (#9b — session-recovery banner also showing):
  // offline chrome keeps priority (grey header/mug/retry are still the most
  // actionable thing — reconnect the network before a reset login can even
  // be attempted), but "Stamping paused" is appended below the offline copy
  // instead of being silently dropped, so a customer who is BOTH offline
  // and locked out of signing still sees both reasons stamping is disabled.
  if (offline) {
    return (
      <div className="flex flex-col items-center">
        <div className="relative mt-4">
          <BrewMug isWaiting active={false} onClick={() => {}} />
        </div>
        <p className="font-grotesk font-bold text-[16px] text-body-grey mt-4 text-center">
          Waiting for a signal
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="pixel-corners-sm bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[13px] py-3 px-5 mt-4 hover:border-coral hover:text-coral"
        >
          Try again
        </button>
        {paused && (
          <p className="font-grotesk font-medium text-[14px] text-body-grey mt-4 text-center">
            Stamping paused
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col items-center">
        {/* Shop logo + name */}
        {hasShop && shopName ? (
          <div className="flex flex-col items-center mb-2.5">
            <PixelLogo
              imageUrl={shopImageUrl}
              pixelImageUrl={shopPixelImageUrl}
              shopName={shopName}
              size={76}
            />
            <div
              className="text-center font-grotesk font-bold text-sm text-primary mt-2"
              style={accentColor ? { color: accentColor } : undefined}
            >
              {shopName}
            </div>
            {windowBadge(stampWindows) && (
              <p className="font-heading text-[9px] text-coral mt-2">
                {windowBadge(stampWindows)}
              </p>
            )}
          </div>
        ) : (
          <div className="text-center mb-4">
            <p className="font-grotesk font-medium text-[13px] text-body-grey">Where do you get coffee?</p>
            <button onClick={onFindShop} className="font-grotesk font-medium text-xs mt-1 inline-block text-coral">
              Find a shop near you →
            </button>
          </div>
        )}

        {/* Pixel mug — paused (#9b, session recovery banner showing) dims to
            opacity-40 per the mockup, distinct from the lighter opacity-50
            no-shop dimming below it. */}
        <div
          // mt-14 clears the steam columns (they rise ~64px above the cup) so
          // they no longer overlap the shop name (Nik's on-device call
          // 2026-08-06: mug lower, steam visible on cream).
          className={`relative mt-10 ${
            paused ? 'opacity-40 pointer-events-none' : !hasShop ? 'opacity-50 pointer-events-none' : ''
          }`}
        >
          <BrewMug isWaiting={isWaiting || !hasShop || !!paused} active={hasShop && !paused} onClick={handleClick} />
        </div>

        {/* Prompt */}
        {paused ? (
          <p className="font-grotesk font-medium text-[14px] text-body-grey mt-4 text-center">
            Stamping paused
          </p>
        ) : isWaiting && hasShop ? (
          <>
            <p className="font-heading text-[9px] text-body-grey mt-4 text-center animate-blink">
              WAITING FOR BARISTA
            </p>
            <p className="font-grotesk font-medium text-[13px] text-body-grey mt-3 text-center">
              No need to tap again
            </p>
          </>
        ) : (
          <p className="font-grotesk font-medium text-[14px] text-body-grey mt-4 text-center animate-blink">
            {!hasShop
              ? 'Find a shop to start'
              : firstStamp
                ? 'Tap the mug for your first stamp'
                : 'Tap the mug to brew'}
          </p>
        )}

        {statusLine && (
          <p className="font-grotesk font-medium text-[12px] text-body-grey mt-2 text-center min-h-[1rem]">
            {statusLine}
          </p>
        )}
      </div>

      {/* Floating Texts */}
      <AnimatePresence>
        {floatingTexts.map((ft) => (
          <motion.div
            key={ft.id}
            initial={{ opacity: 1, y: ft.y, x: ft.x }}
            animate={{ opacity: 0, y: ft.y - 100 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="fixed text-sm md:text-xl font-bold pointer-events-none z-50 font-heading text-success"
            style={accentColor ? { color: accentColor } : undefined}
          >
            {ft.text}
          </motion.div>
        ))}
      </AnimatePresence>
    </>
  );
}
